import React, { useMemo } from 'react';
import { Mafs, Polygon, Line, Coordinates, Theme, Point, Text } from 'mafs';

export const GeometryProperties = ({ config = {} }) => {
    const shapeTypeRaw = config.shapeType ?? 'SQUARE';
    // Backend may serialize enum as "square" (displayName) or "SQUARE" (name) depending on Jackson settings.
    const shapeType = String(shapeTypeRaw).trim().replace('-', '_').replace(' ', '_').toUpperCase();

    const features = config.features ?? {};
    const viewBox = { x: [-5, 5], y: [-5, 5] };

    // --- vertex coercion (Point2D OR [x,y]) ---
    const toXY = (v) => {
        if (!v) return null;
        if (Array.isArray(v) && v.length >= 2) {
            const x = Number(v[0]);
            const y = Number(v[1]);
            if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
            return [x, y];
        }
        if (typeof v === 'object') {
            const x = Number(v.x);
            const y = Number(v.y);
            if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
            return [x, y];
        }
        return null;
    };

    const vertices = useMemo(() => {
        const raw = features.vertices ?? SHAPES[shapeType] ?? SHAPES.SQUARE;
        const mapped = (raw || []).map(toXY).filter(Boolean);
        // If vertices are malformed, fall back to library defaults
        if (mapped.length < 3) return (SHAPES[shapeType] ?? SHAPES.SQUARE).map(toXY).filter(Boolean);
        return mapped;
    }, [shapeType, features.vertices]);

    const isTriangle = vertices.length === 3;

    const center = useMemo(() => {
        const n = vertices.length || 1;
        const sx = vertices.reduce((acc, p) => acc + p[0], 0);
        const sy = vertices.reduce((acc, p) => acc + p[1], 0);
        return [sx / n, sy / n];
    }, [vertices]);

    // --- Parallel side grouping (same as your logic, just extracted) ---
    const parallelPairs = useMemo(() => {
        if (!features.showParallelMarkers || isTriangle) return [];

        const horizontal = [[0, 1], [3, 2]];
        const vertical = [[1, 2], [0, 3]];

        switch (shapeType) {
            case 'SQUARE':
            case 'RECTANGLE':
            case 'RHOMBUS':
            case 'PARALLELOGRAM':
                return [
                    { indices: horizontal, type: 'SINGLE' },
                    { indices: vertical, type: 'DOUBLE' },
                ];
            case 'TRAPEZIUM':
                return [{ indices: horizontal, type: 'SINGLE' }];
            default:
                return [];
        }
    }, [shapeType, features.showParallelMarkers, isTriangle]);

    // --- Equal side grouping (tick marks) ---
    const equalSideGroups = useMemo(() => {
        if (!features.showEqualSideMarkers) return [];
        if (isTriangle) {
            if (shapeType === 'EQUILATERAL_TRIANGLE') {
                return [{ edges: [[0, 1], [1, 2], [2, 0]], ticks: 1 }];
            }
            if (shapeType === 'ISOSCELES_TRIANGLE') {
                // Standard SHAPES puts apex at top => equal sides are (0-2) and (1-2)
                return [{ edges: [[0, 2], [1, 2]], ticks: 1 }];
            }
            return [];
        }

        switch (shapeType) {
            case 'SQUARE':
                return [{ edges: [[0, 1], [1, 2], [2, 3], [3, 0]], ticks: 1 }];
            case 'RHOMBUS':
                return [{ edges: [[0, 1], [1, 2], [2, 3], [3, 0]], ticks: 2 }];
            case 'KITE':
                // Standard SHAPES uses symmetry vertical => adjacent equal pairs
                return [
                    { edges: [[0, 1], [0, 3]], ticks: 1 },
                    { edges: [[2, 1], [2, 3]], ticks: 2 },
                ];
            default:
                return [];
        }
    }, [features.showEqualSideMarkers, isTriangle, shapeType]);

    // --- Symmetry axes ---
    const symmetryAxes = useMemo(() => {
        if (!features.showSymmetryAxes) return [];
        const [cx, cy] = center;

        // For your canonical SHAPES these axes are correct.
        switch (shapeType) {
            case 'SQUARE':
                return [
                    { p1: [-5, cy], p2: [5, cy] },
                    { p1: [cx, -5], p2: [cx, 5] },
                    { p1: [-5, -5], p2: [5, 5] },
                    { p1: [-5, 5], p2: [5, -5] },
                ];
            case 'RECTANGLE':
            case 'RHOMBUS':
                return [
                    { p1: [-5, cy], p2: [5, cy] },
                    { p1: [cx, -5], p2: [cx, 5] },
                ];
            case 'KITE':
            case 'ISOSCELES_TRIANGLE':
                return [{ p1: [cx, -5], p2: [cx, 5] }];
            case 'EQUILATERAL_TRIANGLE':
                // For equilateral, the main symmetry axis is vertical in our default SHAPE
                return [{ p1: [cx, -5], p2: [cx, 5] }];
            default:
                return [];
        }
    }, [features.showSymmetryAxes, shapeType, center]);

    // --- Right angles (rectangle/square) ---
    const rightAngleCorners = useMemo(() => {
        if (!features.showRightAngles) return [];
        if (shapeType !== 'SQUARE' && shapeType !== 'RECTANGLE') return [];
        if (vertices.length !== 4) return [];

        // corners are every vertex using prev and next
        return [0, 1, 2, 3].map((i) => {
            const prev = vertices[(i + 3) % 4];
            const at = vertices[i];
            const next = vertices[(i + 1) % 4];
            return { prev, at, next };
        });
    }, [features.showRightAngles, shapeType, vertices]);

    return (
        <div className="flex flex-col border rounded-xl overflow-hidden bg-white shadow-sm">
            <div className="h-96 w-full bg-slate-50">
                <Mafs viewBox={viewBox} pan={false}>
                    <Coordinates.Cartesian subdivisions={false} />

                    {/* Primary Shape */}
                    <Polygon points={vertices} color={Theme.blue} weight={3} fillOpacity={0.05} />

                    {/* Diagonals */}
                    {features.showDiagonals && !isTriangle && vertices.length === 4 && (
                        <>
                            <Line.Segment point1={vertices[0]} point2={vertices[2]} style="dashed" color={Theme.red} opacity={0.6} />
                            <Line.Segment point1={vertices[1]} point2={vertices[3]} style="dashed" color={Theme.red} opacity={0.6} />
                        </>
                    )}

                    {/* Symmetry Axes */}
                    {symmetryAxes.map((ax, i) => (
                        <Line.Segment
                            key={`sym-${i}`}
                            point1={ax.p1}
                            point2={ax.p2}
                            style="dashed"
                            color={Theme.green}
                            opacity={0.45}
                            weight={2}
                        />
                    ))}

                    {/* Parallel Markers */}
                    {parallelPairs.map((group, gIdx) => (
                        <React.Fragment key={gIdx}>
                            {group.indices.map((pair, pIdx) => (
                                <ParallelMarker
                                    key={`${gIdx}-${pIdx}`}
                                    p1={vertices[pair[0]]}
                                    p2={vertices[pair[1]]}
                                    double={group.type === 'DOUBLE'}
                                />
                            ))}
                        </React.Fragment>
                    ))}

                    {/* Equal Side Tick Marks */}
                    {equalSideGroups.map((g, gi) => (
                        <React.Fragment key={`eq-${gi}`}>
                            {g.edges.map(([a, b], ei) => (
                                <EqualSideMarker key={`eq-${gi}-${ei}`} p1={vertices[a]} p2={vertices[b]} ticks={g.ticks} />
                            ))}
                        </React.Fragment>
                    ))}

                    {/* Right Angle Markers */}
                    {rightAngleCorners.map((c, i) => (
                        <RightAngleMarker key={`ra-${i}`} prev={c.prev} at={c.at} next={c.next} />
                    ))}

                    {/* Vertex Labels */}
                    {vertices.map((v, i) => (
                        <React.Fragment key={i}>
                            <Point x={v[0]} y={v[1]} color={Theme.blue} size={4} />
                            <Text x={v[0]} y={v[1]} attach="nw" size={14} weight="bold">
                                {String.fromCharCode(65 + i)}
                            </Text>
                        </React.Fragment>
                    ))}
                </Mafs>
            </div>

            {/* Properties List */}
            <div className="p-4 border-t bg-white">
                <h4 className="font-bold text-blue-900 mb-2 uppercase text-sm">{shapeType.replace('_', ' ')} Properties</h4>
                <ul className="text-xs text-slate-600 grid grid-cols-2 gap-x-4">
                    {getPropertyList(shapeType).map((p, i) => (
                        <li key={i} className="flex items-center gap-1">
                            <span className="text-blue-500 font-bold">✓</span> {p}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

// --- Markers ---

const ParallelMarker = ({ p1, p2, double }) => {
    const midX = (p1[0] + p2[0]) / 2;
    const midY = (p1[1] + p2[1]) / 2;

    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

    return (
        <g transform={`translate(${midX}, ${midY}) rotate(${angle})`}>
            <circle r="8" fill="white" fillOpacity="0.8" />
            <path d="M -4 -4 L 0 0 L -4 4" fill="none" stroke="black" strokeWidth="2" />
            {double && <path d="M 0 -4 L 4 0 L 0 4" fill="none" stroke="black" strokeWidth="2" />}
        </g>
    );
};

const EqualSideMarker = ({ p1, p2, ticks = 1 }) => {
    const midX = (p1[0] + p2[0]) / 2;
    const midY = (p1[1] + p2[1]) / 2;

    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];

    // unit perpendicular
    const len = Math.hypot(dx, dy) || 1;
    const px = -dy / len;
    const py = dx / len;

    const tickLen = 0.25;
    const gap = 0.12;

    const renderTick = (offset) => (
        <Line.Segment
            point1={[midX + px * (offset - tickLen), midY + py * (offset - tickLen)]}
            point2={[midX + px * (offset + tickLen), midY + py * (offset + tickLen)]}
            color={Theme.foreground}
            weight={2}
        />
    );

    return (
        <>
            {ticks === 1 && renderTick(0)}
            {ticks === 2 && (
                <>
                    {renderTick(-gap)}
                    {renderTick(gap)}
                </>
            )}
        </>
    );
};

const RightAngleMarker = ({ prev, at, next }) => {
    // unit vectors from at->prev and at->next
    const v1 = [prev[0] - at[0], prev[1] - at[1]];
    const v2 = [next[0] - at[0], next[1] - at[1]];

    const n1 = norm(v1);
    const n2 = norm(v2);

    const s = 0.4; // marker size

    const pA = [at[0] + n1[0] * s, at[1] + n1[1] * s];
    const pB = [at[0] + n2[0] * s, at[1] + n2[1] * s];
    const pC = [pA[0] + n2[0] * s, pA[1] + n2[1] * s];

    return (
        <>
            <Line.Segment point1={at} point2={pA} color={Theme.red} weight={2} />
            <Line.Segment point1={at} point2={pB} color={Theme.red} weight={2} />
            <Line.Segment point1={pA} point2={pC} color={Theme.red} weight={2} />
        </>
    );
};

const norm = ([x, y]) => {
    const l = Math.hypot(x, y) || 1;
    return [x / l, y / l];
};

// --- Properties + Default Shapes ---

const getPropertyList = (type) => {
    switch (type) {
        case 'SQUARE':
            return [
                '4 equal sides',
                '4 right angles',
                'Opposite sides parallel',
                'Diagonals bisect each other',
                'Diagonals are equal',
                '4 lines of symmetry',
            ];
        case 'RECTANGLE':
            return [
                'Opposite sides equal',
                '4 right angles',
                'Opposite sides parallel',
                'Diagonals are equal',
                'Diagonals bisect each other',
                '2 lines of symmetry',
            ];
        case 'RHOMBUS':
            return [
                '4 equal sides',
                'Opposite sides parallel',
                'Diagonals bisect each other',
                'Diagonals are perpendicular',
                '2 lines of symmetry',
            ];
        case 'PARALLELOGRAM':
            return [
                'Opposite sides equal',
                'Opposite sides parallel',
                'Opposite angles equal',
                'Diagonals bisect each other',
            ];
        case 'TRAPEZIUM':
            return [
                'One pair of parallel sides',
                'Angles on the same leg sum to 180°',
            ];
        case 'KITE':
            return [
                'Two pairs of adjacent equal sides',
                'One line of symmetry',
                'Diagonals are perpendicular',
            ];
        case 'ISOSCELES_TRIANGLE':
            return [
                'Two equal sides',
                'Base angles are equal',
                'One line of symmetry',
            ];
        case 'EQUILATERAL_TRIANGLE':
            return [
                'Three equal sides',
                'Three equal angles (60°)',
                'Three lines of symmetry',
            ];
        default:
            return [];
    }
};

// Canonical vertex sets (kept axis-aligned for overlay correctness)
const SHAPES = {
    KITE: [
        { x: 0, y: -3 },
        { x: 1.5, y: 0 },
        { x: 0, y: 2 },
        { x: -1.5, y: 0 },
    ],
    RHOMBUS: [
        { x: 0, y: -2 },
        { x: 3, y: 0 },
        { x: 0, y: 2 },
        { x: -3, y: 0 },
    ],
    PARALLELOGRAM: [
        { x: -2, y: -1.5 },
        { x: 2, y: -1.5 },
        { x: 3, y: 1.5 },
        { x: -1, y: 1.5 },
    ],
    TRAPEZIUM: [
        { x: -3, y: -1.5 },
        { x: 3, y: -1.5 },
        { x: 1, y: 1.5 },
        { x: -1, y: 1.5 },
    ],
    SQUARE: [
        { x: -2, y: -2 },
        { x: 2, y: -2 },
        { x: 2, y: 2 },
        { x: -2, y: 2 },
    ],
    RECTANGLE: [
        { x: -3, y: -1.5 },
        { x: 3, y: -1.5 },
        { x: 3, y: 1.5 },
        { x: -3, y: 1.5 },
    ],
    ISOSCELES_TRIANGLE: [
        { x: -2.5, y: -2 },
        { x: 2.5, y: -2 },
        { x: 0, y: 2.5 },
    ],
    EQUILATERAL_TRIANGLE: [
        { x: -2.5, y: -2 },
        { x: 2.5, y: -2 },
        { x: 0, y: 2.33 },
    ],
};
